import { DispatchType } from "@bridgesplit/react";
import { History } from "@mui/icons-material";
import { AppButton, BalanceDisplay } from "app/components/common";
import { TokenInputWithPrice } from "app/components/common/util";
import {
    getZcLedger,
    RefinanceOption,
    formatDurationWithTypeShorthand,
    getZcLoanCollateral
} from "@bridgesplit/abf-react";
import { Column, TagTextAlert } from "@bridgesplit/ui";
import { TransactionTypes } from "app/types";
import { AppDialog, useAppDialog } from "app/utils";
import { COPY } from "app/constants";
import { snakeCaseToTitleCase, SPACE } from "@bridgesplit/utils";

import { IncreasePrincipalQuotes } from "./payments/hooks/types";
import { ActionProps } from "./types";
import { useSubmitIncreasePrincipal } from "./payments/hooks";
import { ActionsSettingsButton } from "./common";
import { LoanPositionStats } from "./LoanPositionStats";

export default function IncreasePrincipal({
    loanExpanded,
    loopPosition,
    isNestedDialogView = false,
    setIsAdjustingTerms,
    increasePrincipalQuote,
    additionalPrincipal,
    setAdditionalPrincipal,
    setSelectedOption
}: ActionProps & {
    isNestedDialogView?: boolean;
    setIsAdjustingTerms?: DispatchType<boolean>;
    increasePrincipalQuote: IncreasePrincipalQuotes;
    additionalPrincipal: number | undefined;
    setAdditionalPrincipal: DispatchType<number | undefined>;
    setSelectedOption: DispatchType<RefinanceOption | undefined>;
}) {
    const { option, validOptions, optionsWithOffsets, additionalPrincipalLimit, maxBorrowAlert, isRefinance } =
        increasePrincipalQuote;
    const { open } = useAppDialog();

    const { submit } = useSubmitIncreasePrincipal({
        loanExpanded,
        option,
        additionalPrincipal,
        additionalPrincipalLimit,
        isRefinance
    });

    const firstLedger = getZcLedger(loanExpanded);
    const currentDebt = firstLedger?.ledgerDebt.total ?? 0;
    const newPrincipalAmount = currentDebt + (additionalPrincipal ?? 0);

    const currentCollateral = getZcLoanCollateral(loanExpanded)?.amount ?? 0;
    const newCollateralAmount = currentCollateral;

    return (
        <Column spacing={3}>
            <Column>
                <Column spacing={0.5}>
                    <ActionsSettingsButton
                        label="Amount"
                        icon={<History fontSize="inherit" sx={{ color: "secondary.main" }} />}
                        rowEndText={`${formatDurationWithTypeShorthand(option?.strategyDuration)}`}
                        onClick={() => {
                            if (isNestedDialogView) {
                                return setIsAdjustingTerms?.(true);
                            }
                            return open(AppDialog.UpdateTerms, {
                                option: option,
                                validOptions: validOptions,
                                optionsWithOffsets: optionsWithOffsets,
                                optionsWithQuotes: validOptions,
                                setOption: setSelectedOption
                            });
                        }}
                    />
                    <TokenInputWithPrice
                        value={additionalPrincipal}
                        metadata={loanExpanded?.principalMetadata}
                        selectToken={null}
                        setValue={setAdditionalPrincipal}
                        hideUsdPrice={false}
                        belowInput={
                            <BalanceDisplay
                                isLoading={!option?.bestQuote || !additionalPrincipalLimit}
                                balance={additionalPrincipalLimit?.additionalBorrowLimit ?? 0}
                                metadata={loanExpanded?.principalMetadata}
                                setMax={() =>
                                    setAdditionalPrincipal(additionalPrincipalLimit?.additionalBorrowLimit ?? 0)
                                }
                                insufficientBalance={false}
                                maxText="Available"
                            />
                        }
                    />
                </Column>
            </Column>

            <LoanPositionStats
                loopPosition={loopPosition}
                newBorrowApy={option?.bestQuote?.apy}
                newStrategyLtv={option?.bestQuote?.ltv}
                newLiquidationThreshold={option?.bestQuote?.liquidationThreshold}
                loanExpanded={loanExpanded}
                newPrincipalAmount={newPrincipalAmount}
                newCollateralAmount={newCollateralAmount}
                isFullRepay={false}
                newDueDate={additionalPrincipal ? option?.loanEndDate : undefined}
                loading={!option}
                show={{
                    debtAndCollateral: true,
                    health: !!additionalPrincipal,
                    netApyStat: !!additionalPrincipal
                }}
            />

            <AppButton
                isTransaction
                transactionType={TransactionTypes.BorrowMore}
                disabled={
                    !option?.bestQuote ||
                    !additionalPrincipal ||
                    !additionalPrincipalLimit?.additionalBorrowLimit ||
                    additionalPrincipal > additionalPrincipalLimit.additionalBorrowLimit
                }
                asyncCta={{ onClickWithResult: submit }}
                fullWidth
            >
                {snakeCaseToTitleCase(COPY.BORROW_MORE_TERM, SPACE)}
            </AppButton>
            {maxBorrowAlert && (
                <TagTextAlert color="warning" icon="warning">
                    {maxBorrowAlert}
                </TagTextAlert>
            )}
        </Column>
    );
}
